import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ConfirmationDialogComponent } from '@app/components';
import { Employee } from '@app/models/employee/employee.model';
import { TaxCredit } from '@app/models/employee/payroll/tax-credit.model';
import { TaxCreditYear } from '@app/models/employee/payroll/types';
import {
    TaxCreditTable,
    TaxCreditTableEvent,
} from '@app/modules/employees/services/tables/tax-credits/tax-credits.table';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { TableModule } from '@app/modules/table/table.module';
import { PlatformModule } from '@app/platform.module';
import { NotifyService } from '@app/services';
import { Jurisdiction } from '@app/types/province';
import { Locale } from '@app/types/translatable.type';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { TaxCreditForm } from '../tax-credit-form/tax-credit-form.component';

export type TaxCreditTableLoadEvent = { isEmpty: boolean };

@Component({
    templateUrl: './tax-credit-table.template.html',
    selector: 'app-tax-credit-table[employee]',
    styleUrls: ['./tax-credit-table.style.scss'],
    standalone: true,
    imports: [TranslateModule, CommonModule, PlatformModule, TaxCreditForm, TableModule],
})
export class TaxCreditTableComponent implements OnDestroy, OnInit {
    @ViewChild(TaxCreditForm) taxCreditForm?: TaxCreditForm;
    @ViewChild('confirmDeactivateDialog') confirmDeactivateDialog!: ConfirmationDialogComponent;

    @Input() jurisdiction: Jurisdiction = 'provincial';
    @Input() employee!: Employee;

    @Output() onDataLoad: EventEmitter<TaxCreditTableLoadEvent> = new EventEmitter<TaxCreditTableLoadEvent>();

    currentlyOpenDialog?: 'CREATE' | 'VIEW'; // View only form when selecting an existing tax credit from the table

    taxCreditTable?: TaxCreditTable;

    readonly INSTRUCTIONS_ID = 'tax-credit-instructions';

    // canCreateTaxCredit dictates whether the button to submit the form and create a tax credit is enabled
    canCreateTaxCredit = true;
    taxCredit = new TaxCredit();
    isLoading = false;
    private _formChanges?: Subscription;
    private _onDestroy$: Subject<void> = new Subject();
    private locale!: Locale;

    constructor(
        private notifyService: NotifyService,
        translateService: TranslateService
    ) {
        this.setCurrentLanguage(translateService.currentLang as Locale);
        translateService.onLangChange
            .pipe(takeUntil(this._onDestroy$))
            .subscribe(({ lang }) => this.setCurrentLanguage(lang as Locale));
    }

    // Sends request to the BE to create Tax Credit
    async createTaxCredit(): Promise<void> {
        if (!this.taxCreditForm?.form.valid) {
            return;
        }
        this.isLoading = true;
        this.canCreateTaxCredit = false;
        try {
            await this.taxCredit.param('employee', this.employee.id).save();
            this.notifyService.success({
                key: this.translationPrefix + 'create.success',
                params: { year: this.taxCredit.year },
            });
            this.closeDialog();
        } catch (e) {
            this.notifyService.error({
                key: this.translationPrefix + 'create.failure',
                params: { year: this.taxCredit.year },
            });
        } finally {
            this.taxCreditTable?.reload();
            this.canCreateTaxCredit = true;
            this.isLoading = false;
        }
    }

    // Sends request to the BE to deactivate an active Tax Credit
    async deactivateTaxCredit(): Promise<void> {
        this.isLoading = true;
        try {
            this.taxCredit.state = 'inactive';
            await this.taxCredit.param('employee', this.employee.id).save();
            this.notifyService.success({
                key: 'employees.jobAndPay.taxCredits.tableActions.deactivate.success',
                params: { year: this.taxCredit.year },
            });
        } catch (e) {
            this.notifyService.error({
                key: 'employees.jobAndPay.taxCredits.tableActions.deactivate.failure',
                params: { year: this.taxCredit.year },
            });
        } finally {
            this.taxCredit.revert();
            this.taxCreditTable?.reload();
            this.isLoading = false;
        }
    }

    ngOnInit(): void {
        // Create table with current locale to ensure proper currency formatting
        this.taxCreditTable = new TaxCreditTable(this.employee.id, this.jurisdiction, this.locale);
        // Send an initial load event when the table finishes loading data
        this.taxCreditTable.onLoading
            .pipe(takeWhile((isLoading) => isLoading))
            .subscribe({ complete: () => this.onDataLoad.emit({ isEmpty: !!this.taxCreditTable?.isEmpty }) });
    }

    ngOnDestroy(): void {
        this._formChanges?.unsubscribe();
        this._onDestroy$.next();
        this._onDestroy$.complete();
    }

    get translationPrefix(): string {
        return `employees.jobAndPay.taxCredits.${this.jurisdiction}.`;
    }

    closeDialog(): void {
        // ! This is required to prevent an ExpressionChangedAfterItHasBeenCheckedError
        // Angular Material throws an error if a form field is focused and we hide the dialog
        // By focusing the header before closing the dialog, we ensure this error doesn't occur.
        document.getElementById(this.INSTRUCTIONS_ID)?.focus();
        this.currentlyOpenDialog = undefined;
        this.taxCredit.revert();
        this._formChanges?.unsubscribe();
        this.canCreateTaxCredit = true; // Reset to initial state after closing the dialog
    }

    openDialog(taxCredit?: TaxCredit): void {
        const jurisdiction = this.jurisdiction === 'federal' ? 'Canada' : undefined;
        this.taxCredit = taxCredit ?? new TaxCredit({ jurisdiction });
        this.currentlyOpenDialog = taxCredit ? 'VIEW' : 'CREATE';
    }

    onOpenDialog(): void {
        if (this.currentlyOpenDialog === 'VIEW') {
            this.taxCreditForm?.selectedYear.set(parseInt(this.taxCredit.year) as TaxCreditYear);
            this.taxCreditForm?.form.form.disable();
        } else {
            this.taxCreditForm?.clearYearAndJurisdictionSelections();
            this.taxCreditForm?.form.form.enable();
        }

        // Only allow submissions when the form is valid
        this.canCreateTaxCredit = !!this.taxCreditForm?.valid;
        this._formChanges = this.taxCreditForm?.form.statusChanges?.subscribe((status: 'VALID' | 'INVALID') => {
            this.canCreateTaxCredit = status === 'VALID';
        });
    }

    async onTableMenuAction(menuColumnEvent: MenuColumnEvent<TaxCredit, TaxCreditTableEvent>): Promise<void> {
        if (menuColumnEvent.data) {
            this.taxCredit = menuColumnEvent.data;
            if (menuColumnEvent.menuItem.event === 'view') {
                this.currentlyOpenDialog = 'VIEW';
            }
            if (menuColumnEvent.menuItem.event === 'deactivate') {
                if (await this.confirmDeactivateDialog.open()) {
                    this.deactivateTaxCredit();
                }
            }
        }
    }

    private setCurrentLanguage(locale: Locale): void {
        this.locale = locale;
    }
}
